import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../LanguageContext";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "products", label: t.nav.collections },
    { id: "defense", label: t.nav.defense },
    { id: "about", label: t.nav.about },
    { id: "partners", label: t.nav.partners },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") { navigate(`/#${id}`); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const goPolicy = (type) => { setOpen(false); navigate(`/policy/${type}`); };

  const dark = !scrolled; // on hero it's light text

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
      className={"fixed top-0 left-0 right-0 z-50 transition-colors duration-500 " +
        (scrolled ? "bg-paper/90 backdrop-blur-md border-b border-border" : "bg-transparent")}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center group">
          <img src={dark ? "/horns-logo-dark.webp" : "/horns-logo.webp"} alt="Horns Industriservice"
            className="h-12 md:h-14 w-auto transition-all duration-500" />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)}
              className={"nav-link text-[13px] uppercase tracking-[0.18em] font-light transition-colors duration-500 " + (dark ? "text-white/90 hover:text-white" : "text-ink/80 hover:text-ink")}>
              {l.label}
            </button>
          ))}
          <div className="relative group">
            <button data-testid="nav-policy-trigger"
              className={"nav-link text-[13px] uppercase tracking-[0.18em] font-light transition-colors duration-500 " + (dark ? "text-white/90 hover:text-white" : "text-ink/80 hover:text-ink")}>
              Policy
            </button>
            <div className="absolute right-0 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
              <div className="bg-paper border border-border shadow-2xl rounded-sm py-2 min-w-[230px]">
                <button onClick={() => goPolicy("quality")} data-testid="nav-policy-quality"
                  className="block w-full text-left px-6 py-3 text-[13px] tracking-[0.06em] text-ink/80 hover:text-ink hover:bg-paper-warm transition-colors">
                  {t.policies?.quality?.label || "Kvalitetspolicy"}
                </button>
                <button onClick={() => goPolicy("environment")} data-testid="nav-policy-environment"
                  className="block w-full text-left px-6 py-3 text-[13px] tracking-[0.06em] text-ink/80 hover:text-ink hover:bg-paper-warm transition-colors">
                  {t.policies?.environment?.label || "Miljöpolicy"}
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="flex items-center gap-5">
          <div className={"hidden sm:flex items-center text-[13px] tracking-[0.15em] font-light " + (dark ? "text-white/70" : "text-ink/60")}>
            <button onClick={() => setLang("sv")} className={"px-1 transition-colors " + (lang === "sv" ? (dark ? "text-white" : "text-ink") + " font-medium" : "hover:text-gold")}>SV</button>
            <span className="opacity-40">/</span>
            <button onClick={() => setLang("en")} className={"px-1 transition-colors " + (lang === "en" ? (dark ? "text-white" : "text-ink") + " font-medium" : "hover:text-gold")}>EN</button>
          </div>
          <button className={"md:hidden " + (dark ? "text-white" : "text-ink")} onClick={() => setOpen(true)} aria-label="Menu">
            <Menu size={24} strokeWidth={1.2} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink text-white flex flex-col">
            <div className="h-[76px] px-6 flex items-center justify-between">
              <img src="/horns-logo-dark.webp" alt="Horns" className="h-7 w-auto" />
              <button onClick={() => setOpen(false)} aria-label="Close"><X size={26} strokeWidth={1.2} /></button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-7">
              {links.map((l, i) => (
                <motion.button key={l.id} onClick={() => go(l.id)}
                  initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
                  className="text-left font-serif text-4xl">
                  {l.label}
                </motion.button>
              ))}
              <motion.button onClick={() => goPolicy("quality")}
                initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + links.length * 0.08 }}
                className="text-left font-serif text-4xl">
                {t.policies?.quality?.label || "Kvalitetspolicy"}
              </motion.button>
              <motion.button onClick={() => goPolicy("environment")}
                initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + (links.length + 1) * 0.08 }}
                className="text-left font-serif text-4xl">
                {t.policies?.environment?.label || "Miljöpolicy"}
              </motion.button>
            </div>
            <div className="px-8 pb-10 flex gap-4 text-sm tracking-[0.2em]">
              <button onClick={() => { setLang("sv"); }} className={lang === "sv" ? "text-gold" : "text-white/60"}>SVENSKA</button>
              <button onClick={() => { setLang("en"); }} className={lang === "en" ? "text-gold" : "text-white/60"}>ENGLISH</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
